/**
 * @fileoverview Date calculation for PDF form builder date fields.
 *
 * A date placement can carry a `dateCalc` config so its value is derived
 * dynamically from another date field instead of being entered manually —
 * e.g. "Records period — From" = 10 years before the Date of Appointment.
 *
 * Resolution happens at fill time (see `jobs/precedents/fillPdfAction.ts`):
 * an explicit value for the placement's own canonical key always wins; the
 * calc only fills the gap when the field is empty. This module is shared
 * between the Convex fill action and the frontend field editor so the
 * builder preview and the filled PDF can never disagree.
 *
 * `business_days` skips weekends and public holidays in the appointee's
 * jurisdiction, reusing the shared calendar engine in `lib/businessDays.ts`
 * (the same one behind workflow due-date offsets).
 */

import { addBusinessDaysSigned } from "@cvx/tasks/offsetDates";
import {
  PROCLAIMED_BASELINE,
  type AuJurisdiction,
  type ProclaimedHoliday,
} from "@cvx/lib/businessDays";

export type DateCalcUnit =
  "days" | "business_days" | "weeks" | "months" | "years";
export type DateCalcDirection = "before" | "after";

export interface DateCalc {
  /** Canonical (or custom) date field key the calculation starts from. */
  baseKey: string;
  /** Whole number of units to offset by. */
  amount: number;
  unit: DateCalcUnit;
  direction: DateCalcDirection;
}

/**
 * Calendar context for `business_days` offsets. Every other unit ignores it,
 * so callers only need to load it when a placement actually uses business
 * days. Without a jurisdiction the count still skips weekends but only
 * nationwide public holidays, which can only ever push the result later than
 * a state-aware calendar would.
 */
export interface DateCalcContext {
  /** Appointee's state or territory — picks the public-holiday calendar. */
  jurisdiction?: AuJurisdiction;
  /** Admin-adjusted proclaimed holidays; defaults to the code baseline. */
  proclaimed?: ProclaimedHoliday[];
}

const DATE_CALC_UNIT_LABELS: Record<
  DateCalcUnit,
  { singular: string; plural: string }
> = {
  days: { singular: "day", plural: "days" },
  business_days: { singular: "business day", plural: "business days" },
  weeks: { singular: "week", plural: "weeks" },
  months: { singular: "month", plural: "months" },
  years: { singular: "year", plural: "years" },
};

export const DATE_CALC_UNITS: Array<{ value: DateCalcUnit; label: string }> = [
  { value: "days", label: "days" },
  { value: "business_days", label: "business days" },
  { value: "weeks", label: "weeks" },
  { value: "months", label: "months" },
  { value: "years", label: "years" },
];

export const DATE_CALC_DIRECTIONS: Array<{
  value: DateCalcDirection;
  label: string;
}> = [
  { value: "before", label: "before" },
  { value: "after", label: "after" },
];

/**
 * Parse the date shapes that flow through the field-values map: ISO
 * (`2025-08-06` / full ISO timestamps) and the rendered `dd/MM/yyyy`.
 * Returns UTC year/month/day parts, or null when unparseable.
 */
function parseDateParts(
  raw: string,
): { year: number; month: number; day: number } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const dmy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    return {
      year: Number(dmy[3]),
      month: Number(dmy[2]) - 1,
      day: Number(dmy[1]),
    };
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    year: parsed.getUTCFullYear(),
    month: parsed.getUTCMonth(),
    day: parsed.getUTCDate(),
  };
}

function toIso(parts: { year: number; month: number; day: number }): string {
  const yyyy = String(parts.year).padStart(4, "0");
  const mm = String(parts.month + 1).padStart(2, "0");
  const dd = String(parts.day).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Apply a date calc to a base date value and return the result as an ISO
 * `yyyy-MM-dd` string (the same shape job dates are stored in, so the
 * existing `formatDateValue` render path picks it up unchanged).
 *
 * Month/year offsets clamp to the end of the target month so e.g.
 * 31 May − 3 months = 29/28 Feb rather than rolling into March. Business-day
 * offsets step over weekends and public holidays using `context`. Returns
 * undefined for a missing/unparseable base value or a non-finite amount so
 * the placement renders blank rather than "NaN".
 */
export function applyDateCalc(
  calc: DateCalc,
  baseValue: string | undefined | null,
  context: DateCalcContext = {},
): string | undefined {
  if (!baseValue) return undefined;
  const parts = parseDateParts(baseValue);
  if (!parts) return undefined;
  if (!Number.isFinite(calc.amount)) return undefined;

  const signed = Math.trunc(
    calc.direction === "before" ? -calc.amount : calc.amount,
  );

  if (calc.unit === "business_days") {
    return addBusinessDaysSigned(
      toIso(parts),
      signed,
      context.jurisdiction,
      context.proclaimed ?? PROCLAIMED_BASELINE,
    );
  }

  const result: Date =
    calc.unit === "months" || calc.unit === "years"
      ? (() => {
          const monthOffset = calc.unit === "years" ? signed * 12 : signed;
          const totalMonths = parts.year * 12 + parts.month + monthOffset;
          const year = Math.floor(totalMonths / 12);
          const month = ((totalMonths % 12) + 12) % 12;
          // Clamp the day to the target month's length (31 Jan + 1 month → 28 Feb).
          const daysInMonth = new Date(
            Date.UTC(year, month + 1, 0),
          ).getUTCDate();
          return new Date(
            Date.UTC(year, month, Math.min(parts.day, daysInMonth)),
          );
        })()
      : (() => {
          const dayOffset = calc.unit === "weeks" ? signed * 7 : signed;
          return new Date(
            Date.UTC(parts.year, parts.month, parts.day + dayOffset),
          );
        })();

  if (Number.isNaN(result.getTime())) return undefined;
  const yyyy = String(result.getUTCFullYear()).padStart(4, "0");
  const mm = String(result.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(result.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Resolve a placement's date calc against the merged field-values map.
 * Convenience wrapper used by the fill action.
 */
export function resolveDateCalc(
  calc: DateCalc | undefined,
  values: Record<string, string>,
  context: DateCalcContext = {},
): string | undefined {
  if (!calc) return undefined;
  return applyDateCalc(calc, values[calc.baseKey], context);
}

/**
 * Resolve a template's whole `dateCalcs` map against a field-values map and
 * return only the keys the calcs actually derive.
 *
 * A value already present for a key wins — the calc fills gaps, it never
 * overwrites what the practitioner typed. Calcs may chain (A derived from B,
 * B derived from C), so this loops until nothing new resolves, bounded by the
 * number of calcs so a cycle terminates instead of hanging.
 */
export function resolveTemplateDateCalcs(
  dateCalcs: Record<string, DateCalc> | undefined,
  values: Record<string, string>,
  context: DateCalcContext = {},
): Record<string, string> {
  const entries = Object.entries(dateCalcs ?? {});
  if (entries.length === 0) return {};

  const derived: Record<string, string> = {};
  const pending = entries.filter(([key]) => !values[key]);

  for (let pass = 0; pass < entries.length && pending.length > 0; pass++) {
    const pendingCountBeforePass = pending.length;
    for (let i = pending.length - 1; i >= 0; i--) {
      const [key, calc] = pending[i];
      const base = derived[calc.baseKey] ?? values[calc.baseKey];
      const result = applyDateCalc(calc, base, context);
      if (result === undefined) continue;
      derived[key] = result;
      pending.splice(i, 1);
    }
    if (pending.length === pendingCountBeforePass) break;
  }

  return derived;
}

/**
 * Human-readable summary for the builder UI, e.g.
 * "10 years before Date of Appointment".
 */
export function describeDateCalc(
  calc: DateCalc,
  labelForKey?: (key: string) => string | undefined,
): string {
  const baseLabel = labelForKey?.(calc.baseKey) ?? calc.baseKey;
  const labels = DATE_CALC_UNIT_LABELS[calc.unit];
  const unit = calc.amount === 1 ? labels.singular : labels.plural;
  return `${calc.amount} ${unit} ${calc.direction} ${baseLabel}`;
}
